import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import { getAthleteExerciseRecords } from '../lib/database';
import SkeletonLoader from './SkeletonLoader';
import BottomNav from './BottomNav';
import './AthleteProgress.css';

function AthleteProgress() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('frequency');

  const fetchRecords = useCallback(
    () => getAthleteExerciseRecords(profile?.id),
    [profile?.id]
  );
  const { data: exercises, loading } = useDataCache(
    profile?.id ? `athlete-exercise-records-${profile.id}` : null,
    fetchRecords,
    120000
  );

  const filteredExercises = useMemo(() => {
    if (!exercises || exercises.length === 0) return [];
    let filtered = [...exercises];

    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(ex =>
        ex.exerciseName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (sortBy === 'name') {
      filtered.sort((a, b) => a.exerciseName.localeCompare(b.exerciseName));
    } else {
      filtered.sort((a, b) => b.totalSessions - a.totalSessions);
    }

    return filtered;
  }, [exercises, searchQuery, sortBy]);

  const handleBack = () => {
    navigate('/athlete');
  };

  const handleExerciseClick = (exerciseId, exerciseName) => {
    navigate(`/athlete/progress/${exerciseId}`, {
      state: { exerciseName }
    });
  };

  if (loading) {
    return (
      <div className="athlete-progress-container page-with-bottom-nav">
        <header className="progress-header">
          <button className="btn-back-minimal" onClick={handleBack}>
            <span className="back-arrow">&larr;</span>
          </button>
          <h1 className="progress-title">Progressi</h1>
        </header>
        <main className="progress-main">
          <SkeletonLoader variant="list" count={5} />
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="athlete-progress-container page-with-bottom-nav">
      <header className="progress-header">
        <button className="btn-back-minimal" onClick={handleBack}>
          <span className="back-arrow">&larr;</span>
        </button>
        <h1 className="progress-title">Progressi</h1>
        <button
          onClick={() => navigate('/athlete/catalog')}
          style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', borderRadius: '999px', border: '1px solid #e5e7eb',
            background: 'white', color: '#4f46e5', fontWeight: 700, fontSize: '14px', cursor: 'pointer'
          }}
        >
          📚 Catalogo
        </button>
      </header>

      <main className="progress-main">
        {exercises && exercises.length > 0 && (
          <div className="progress-filters">
            <div className="search-container">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="search-input"
                placeholder="Cerca esercizio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => setSearchQuery('')}
                >
                  ✕
                </button>
              )}
            </div>

            <div className="sort-container">
              <button
                className={`sort-btn ${sortBy === 'frequency' ? 'active' : ''}`}
                onClick={() => setSortBy('frequency')}
              >
                Frequenza
              </button>
              <button
                className={`sort-btn ${sortBy === 'name' ? 'active' : ''}`}
                onClick={() => setSortBy('name')}
              >
                A-Z
              </button>
            </div>
          </div>
        )}

        {filteredExercises.length > 0 ? (
          <div className="exercises-list-clean">
            {filteredExercises.map(exercise => (
              <div
                key={exercise.exerciseId}
                className="exercise-row"
                onClick={() => handleExerciseClick(exercise.exerciseId, exercise.exerciseName)}
              >
                <span className="exercise-name">{exercise.exerciseName}</span>
                <div className="exercise-meta">
                  <span className="session-count">
                    {exercise.totalSessions}×
                  </span>
                  <span className="row-arrow">&rsaquo;</span>
                </div>
              </div>
            ))}
          </div>
        ) : (!exercises || exercises.length === 0) ? (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <h3>Nessun progresso registrato</h3>
            <p>Completa un allenamento per iniziare a tracciare i tuoi progressi</p>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>Nessun risultato</h3>
            <p>Prova a cercare con un altro termine</p>
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}

export default AthleteProgress;
